import React, { useEffect, useState, useRef } from 'react';
import { Film, Trash2, Download, Play } from 'lucide-react';
import {
  listRecordings,
  deleteRecording,
  formatRecordingDuration,
  formatFileSize,
} from '../../services/recordings.service.ts';
import type { StoredRecording } from '../meeting/types/meeting.types.ts';
import PageHeader from '../dashboard/components/PageHeader.tsx';

const RecordingsPage: React.FC = () => {
  const [recordings, setRecordings] = useState<StoredRecording[]>([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [playUrl, setPlayUrl] = useState<string | null>(null);
  const playUrlRef = useRef<string | null>(null);

  useEffect(() => {
    playUrlRef.current = playUrl;
  }, [playUrl]);

  const load = async () => {
    setLoading(true);
    try {
      setRecordings(await listRecordings());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    return () => {
      if (playUrlRef.current) URL.revokeObjectURL(playUrlRef.current);
    };
  }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    await deleteRecording(id);
    if (playingId === id && playUrl) {
      URL.revokeObjectURL(playUrl);
      setPlayUrl(null);
      setPlayingId(null);
    }
    await load();
  };

  const handleDownload = (recording: StoredRecording) => {
    const url = URL.createObjectURL(recording.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${recording.meetingId}_${recording.createdAt}.webm`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePlay = (recording: StoredRecording) => {
    if (playUrl) URL.revokeObjectURL(playUrl);
    const url = URL.createObjectURL(recording.blob);
    setPlayUrl(url);
    setPlayingId(recording.id);
  };

  const closePlayer = () => {
    if (playUrl) URL.revokeObjectURL(playUrl);
    setPlayUrl(null);
    setPlayingId(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Film}
        title="Kayıtlar"
        description="Toplantı kayıtlarınız bu cihazda saklanır."
      />

      {loading ? (
        <p className="text-gray-400 text-center py-12">Yükleniyor...</p>
      ) : recordings.length === 0 ? (
        <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10">
          <Film className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400">Henüz kayıt yok.</p>
          <p className="text-gray-500 text-sm mt-2">
            Toplantıda alt menüden &quot;Toplantı kaydı al&quot; seçeneğini kullanın.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {recordings.map((recording) => (
            <div
              key={recording.id}
              className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10"
            >
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{recording.title}</p>
                <p className="text-gray-400 text-sm mt-1">
                  {new Date(recording.createdAt).toLocaleString('tr-TR')}
                  {' · '}
                  {formatRecordingDuration(recording.durationSeconds)}
                  {' · '}
                  {formatFileSize(recording.sizeBytes)}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handlePlay(recording)}
                  className="p-2.5 rounded-lg bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 transition"
                  title="Oynat"
                >
                  <Play className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload(recording)}
                  className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition"
                  title="İndir"
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete(recording.id)}
                  className="p-2.5 rounded-lg bg-red-600/20 hover:bg-red-600/40 text-red-300 transition"
                  title="Sil"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {playingId && playUrl && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl border border-white/10 w-full max-w-3xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-white font-semibold">Kayıt oynatılıyor</h3>
              <button
                type="button"
                onClick={closePlayer}
                className="text-gray-400 hover:text-white text-sm"
              >
                Kapat
              </button>
            </div>
            <video src={playUrl} controls autoPlay className="w-full bg-black" />
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordingsPage;
